import React from "react";
import { Link } from "react-router-dom";

/**
 * Linked2Marketing logo — inline SVG.
 * Recreates the uploaded brand: slanted silver L2M with a champagne-gold "2",
 * a spaced uppercase wordmark, and an "AI POWERED. RESULTS DRIVEN." tagline.
 *
 * Variants:
 *  - "mark"  → just the L2M mark (compact, used in header & sticky CTA)
 *  - "full"  → mark + wordmark + tagline lockup (used in footer & mobile menu)
 *
 * The SVG uses viewBox-based scaling so it stays crisp at any height.
 */

const SilverGradient = ({ id }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stopColor="#E9ECF1" />
    <stop offset="0.45" stopColor="#A6ACB6" />
    <stop offset="0.55" stopColor="#9097A1" />
    <stop offset="1" stopColor="#D9DDE3" />
  </linearGradient>
);

const GoldGradient = ({ id }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stopColor="#F4DDA0" />
    <stop offset="0.55" stopColor="#D8B56D" />
    <stop offset="1" stopColor="#9E7A3A" />
  </linearGradient>
);

function L2MMark({ idPrefix }) {
  const silver = `${idPrefix}-silver`;
  const gold = `${idPrefix}-gold`;
  return (
    <g>
      <defs>
        <SilverGradient id={silver} />
        <GoldGradient id={gold} />
      </defs>
      {/* L — slanted, thin stroke */}
      <path
        d="M 4 4 L 18 4 L 18 88 L 60 88 L 60 100 L 4 100 Z"
        fill={`url(#${silver})`}
      />
      {/* 2 — champagne gold, modern slab */}
      <path
        d="M 72 26
           C 72 12, 86 4, 100 4
           C 116 4, 128 14, 128 28
           C 128 40, 122 48, 110 58
           L 80 84 L 130 84 L 130 100 L 70 100 L 70 88
           L 108 50
           C 114 44, 118 38, 118 30
           C 118 22, 112 16, 100 16
           C 90 16, 82 22, 82 32
           L 72 32 Z"
        fill={`url(#${gold})`}
      />
      {/* M — slanted */}
      <path
        d="M 144 100 L 144 4 L 160 4 L 184 64 L 208 4 L 224 4 L 224 100 L 210 100 L 210 32 L 190 80 L 178 80 L 158 32 L 158 100 Z"
        fill={`url(#${silver})`}
      />
    </g>
  );
}

function L2MFull({ idPrefix }) {
  return (
    <g>
      {/* Mark — centered horizontally inside a 260-unit viewBox */}
      <g transform="translate(18 0)">
        <L2MMark idPrefix={idPrefix} />
      </g>
      {/* wordmark */}
      <text
        x="130"
        y="138"
        textAnchor="middle"
        fill="#CDD2D9"
        fontFamily='"Montserrat", system-ui, sans-serif'
        fontSize="16"
        fontWeight="500"
        letterSpacing="2.8"
      >
        LINKED2MARKETING
      </text>
      {/* tagline divider + tagline */}
      <g opacity="0.85">
        <line x1="20" y1="159" x2="56" y2="159" stroke="#888E96" strokeWidth="0.7" />
        <text
          x="130"
          y="163"
          textAnchor="middle"
          fill="#9097A1"
          fontFamily='"Montserrat", system-ui, sans-serif'
          fontSize="8"
          fontWeight="500"
          letterSpacing="2.2"
        >
          AI POWERED. RESULTS DRIVEN.
        </text>
        <line x1="204" y1="159" x2="240" y2="159" stroke="#888E96" strokeWidth="0.7" />
      </g>
    </g>
  );
}

export default function Logo({
  variant = "mark",
  height = 40,
  className = "",
  testId = "site-logo",
  as = "link",
  to = "/",
}) {
  const idPrefix = `l2m-${variant}-${Math.random().toString(36).slice(2, 7)}`;
  const isFull = variant === "full";
  const viewBox = isFull ? "0 0 228 174" : "0 0 228 104";

  const svg = (
    <svg
      viewBox={viewBox}
      style={{ height, width: "auto", display: "block" }}
      aria-hidden="true"
      focusable="false"
    >
      {isFull ? <L2MFull idPrefix={idPrefix} /> : <L2MMark idPrefix={idPrefix} />}
    </svg>
  );

  const alt = "Linked2Marketing — AI powered. Results driven.";

  if (as === "div") {
    return (
      <div
        data-testid={testId}
        className={`inline-flex items-center ${className}`}
        role="img"
        aria-label={alt}
      >
        {svg}
      </div>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex items-center group ${className}`}
      aria-label={`${alt} — home`}
    >
      {svg}
    </Link>
  );
}
